import React, { useState, useEffect } from 'react';

function AmountThresholdsTab({ config, onUpdate }) {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const categories = config?.tenantCategories || ['NSK', 'OTHER'];

  useEffect(() => {
    // Initialize formData with all categories, using existing values or 0 as default
    const existingThresholds = config?.amountThresholds || {};
    const initialData = {};
    categories.forEach(cat => {
      initialData[cat] = existingThresholds[cat] !== undefined ? existingThresholds[cat] : 0;
    });
    setFormData(initialData);
  }, [config, categories.join(',')]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onUpdate({ amountThresholds: formData });
      setMessage({ type: 'success', text: 'Amount thresholds saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleThresholdChange = (category, value) => {
    // If empty string, set to empty (let user clear)
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        [category]: ''
      }));
      return;
    }
    
    // Remove all leading zeros except when it's just "0" or "0."
    let cleanedValue = value.toString();
    
    // If starts with 0 and has more characters and next char is not a dot
    if (cleanedValue.length > 1 && cleanedValue.startsWith('0') && cleanedValue[1] !== '.') {
      // Remove all leading zeros
      cleanedValue = cleanedValue.replace(/^0+/, '');
      // If nothing left, it means it was all zeros, keep one
      if (cleanedValue === '') {
        cleanedValue = '0';
      }
    }
    
    // Parse the value and ensure it's not negative
    const numValue = parseFloat(cleanedValue);
    const finalValue = isNaN(numValue) || numValue < 0 ? 0 : numValue;
    
    setFormData(prev => ({
      ...prev,
      [category]: finalValue
    }));
  };

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>Amount & Threshold Rules</h2>
        <p>Set minimum spend thresholds per tenant category. Claims must meet ALL thresholds to be eligible.</p>
      </div>

      <div className="rules-form">
        <div className="form-section">
          <div className="form-section-title">Spend Thresholds by Category</div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1.25rem 0' }}>
            The total receipt amount for each category must meet or exceed its threshold.
          </p>
          
          <div className="items-list">
            {categories.map(category => (
              <div key={category} className="item-card">
                <div className="item-info">
                  <div className="item-icon" style={{ 
                    background: category === 'NSK' 
                      ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                      : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' 
                  }}>
                    💰
                  </div>
                  <div className="item-details">
                    <h4>{category} Category</h4>
                    <span>Minimum spend required</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#64748b', fontWeight: '500' }}>RM</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData[category] !== undefined ? formData[category] : 0}
                    onInput={(e) => {
                      // Real-time prevention of leading zeros
                      const val = e.target.value;
                      if (val.length > 1 && val.startsWith('0') && val[1] !== '.') {
                        e.target.value = val.replace(/^0+/, '') || '0';
                      }
                    }}
                    onChange={(e) => handleThresholdChange(category, e.target.value)}
                    onKeyDown={(e) => {
                      // Prevent minus sign and 'e' (exponential notation)
                      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                        e.preventDefault();
                      }
                    }}
                    onBlur={(e) => {
                      // On blur, ensure the value is properly formatted
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      setFormData(prev => ({
                        ...prev,
                        [category]: val
                      }));
                    }}
                    style={{
                      width: '120px',
                      padding: '0.5rem 0.75rem',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      textAlign: 'right'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-section" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.25rem' }}>📋</span>
            <div>
              <strong style={{ color: '#166534' }}>How It Works</strong>
              <p style={{ color: '#15803d', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
                A claim is eligible only when <strong>ALL</strong> category thresholds are met. 
                For example, if a user submits receipts totaling RM400 from NSK and RM150 from OTHER, 
                the claim would be <strong>ineligible</strong> because OTHER threshold (RM{formData.OTHER || 188}) is not met.
              </p>
            </div>
          </div>
        </div>

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        <div className="save-bar">
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AmountThresholdsTab;
